import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Camera, MapPin, Truck, Check, X as XIcon, CheckCircle2, ArrowRight,
  Car, Clapperboard, Music, Shirt, Megaphone, Smartphone, Heart, Lightbulb, AlertTriangle,
} from 'lucide-react';
import VehicleImage from './VehicleImage';
import { Reveal, SectionHeading } from './Reveal';
import { VEHICLES } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING = {
  escalade: { two: 300, four: 400 },
  m4: { two: 250, four: 350 },
  bmw430: { two: 150, four: 250 },
};

const STEPS = [
  { n: '01', icon: Car, title: 'Choose Your Vehicle', text: 'Select the vehicle you want for your shoot.' },
  { n: '02', icon: MapPin, title: 'Choose Your Location', text: 'Tell us where you want the shoot to take place.' },
  { n: '03', icon: Truck, title: 'We Bring the Car', text: 'ALAS Velocity coordinates delivery to your location and retrieves the vehicle when the booking is complete.' },
];

const INCLUDED = [
  'Luxury vehicle for your photoshoot',
  'You choose the shoot location',
  'ALAS coordinates vehicle delivery',
  'Vehicle arrives clean and shoot-ready',
  'Vehicle pickup after the scheduled shoot',
  'Professional booking coordination',
];

const NOT_INCLUDED = [
  'Photography', 'Videography', 'Models', 'Photo/video editing',
  'Props', 'Location fees', 'Permits', 'Event production', 'Other production services',
];

const PERFECT_FOR = [
  { icon: Camera, label: 'Car Photoshoots' },
  { icon: Smartphone, label: 'Social Media Content' },
  { icon: Music, label: 'Music Videos' },
  { icon: Shirt, label: 'Fashion Shoots' },
  { icon: Megaphone, label: 'Brand Campaigns' },
  { icon: Clapperboard, label: 'Influencer Content' },
  { icon: Heart, label: 'Engagement / Couple Shoots' },
  { icon: Lightbulb, label: 'Creative Projects' },
];

const EMPTY = {
  full_name: '', phone: '', email: '', vehicle: '', preferred_date: '', start_time: '',
  duration: '', shoot_location: '', shoot_type: '', num_people: '', stationary: '', repositioned: '', notes: '',
};

const inputCls =
  'w-full border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm font-light text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold/70 focus:bg-white/[0.05]';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-gold/80">{label}</span>
    {children}
  </label>
);

const Photoshoot = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/photoshoot-bookings`, form);
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again or text us at 786-920-2715.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="photoshoot" data-testid="photoshoot-section" className="scroll-mt-24 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <span className="text-xs font-medium uppercase tracking-[0.35em] text-gold">Photoshoot Vehicle Rentals</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-5 text-4xl leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Your shoot. Your location. <span className="italic text-gold">Our cars.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base font-light leading-relaxed text-white/65 sm:text-lg">
              Need a luxury vehicle for your next photoshoot, content shoot, music video, or creative project? Choose your location and ALAS Velocity will bring the vehicle directly to you.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="photoshoot-book-cta" onClick={() => scrollTo('#photoshoot-form')} className="group flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-gold-bright">
                Book a Photoshoot Vehicle
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button data-testid="photoshoot-vehicles-cta" onClick={() => scrollTo('#photoshoot-pricing')} className="border border-gold/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold/10">
                View Vehicles
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div data-testid="photoshoot-notice" className="mt-14 flex items-start gap-5 border border-gold/40 bg-gold/[0.06] p-7 md:items-center">
            <AlertTriangle className="h-8 w-8 shrink-0 text-gold" strokeWidth={1.25} />
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Vehicle Only — Photography Not Included</p>
              <p className="mt-2 text-sm font-light leading-relaxed text-white/60">
                This service provides the vehicle for your shoot. Photography, videography, models, editing, and other production services are not included.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-24 grid gap-px border border-white/5 bg-white/5 md:grid-cols-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.n} delay={i * 0.1} className="h-full">
                <div data-testid={`photoshoot-step-${s.n}`} className="flex h-full flex-col bg-ink p-9">
                  <div className="flex items-center justify-between">
                    <span className="outline-text-gold font-display text-5xl leading-none">{s.n}</span>
                    <Icon className="h-6 w-6 text-gold" strokeWidth={1.25} />
                  </div>
                  <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.2em] text-white">{s.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-white/50">{s.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div id="photoshoot-pricing" className="mt-24 scroll-mt-28">
          <SectionHeading eyebrow="Photoshoot Pricing" title={<>Pick your car.<br /><span className="italic text-gold">Pick your hours.</span></>} />
          <div className="grid gap-8 md:grid-cols-3">
            {VEHICLES.map((v, i) => {
              const p = PRICING[v.id];
              return (
                <Reveal key={v.id} delay={i * 0.1} className="h-full">
                  <article data-testid={`photoshoot-price-${v.id}`} className="group flex h-full flex-col border border-white/5 bg-surface transition-all duration-500 hover:-translate-y-2 hover:border-gold/30">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <VehicleImage src={v.image} alt={v.name} label={v.name.toUpperCase()} />
                      <div className="absolute left-0 top-0 h-[2px] w-0 bg-gold transition-all duration-700 group-hover:w-full" />
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold/80">{v.category}</span>
                      <h3 className="font-display mt-2 text-xl tracking-tight text-white">{v.name}</h3>
                      <div className="mt-6 grid grid-cols-2 gap-px border border-white/10 bg-white/10">
                        <div className="bg-surface p-4 text-center">
                          <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40">2 Hours</span>
                          <span data-testid={`photoshoot-price-2h-${v.id}`} className="font-display mt-1 block text-3xl text-gold">${p.two}</span>
                        </div>
                        <div className="bg-surface p-4 text-center">
                          <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40">4 Hours</span>
                          <span data-testid={`photoshoot-price-4h-${v.id}`} className="font-display mt-1 block text-3xl text-gold">${p.four}</span>
                        </div>
                      </div>
                      <button
                        data-testid={`photoshoot-book-${v.id}`}
                        onClick={() => { setForm((f) => ({ ...f, vehicle: v.name })); scrollTo('#photoshoot-form'); }}
                        className="mt-6 w-full border border-gold/50 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                      >
                        Book This Vehicle
                      </button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 text-center text-xs font-light text-white/40">
              2-hour minimum booking required. Additional time may be available depending on vehicle availability.
            </p>
          </Reveal>
        </div>

        <div className="mt-24 grid gap-px border border-white/5 bg-white/5 lg:grid-cols-2">
          <Reveal className="h-full">
            <div className="h-full bg-ink p-10">
              <h3 className="font-display text-2xl tracking-tight text-white">What's <span className="italic text-gold">included</span></h3>
              <ul className="mt-7 space-y-4">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm font-light text-white/70">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="h-full">
            <div className="h-full bg-ink p-10">
              <h3 className="font-display text-2xl tracking-tight text-white">What's <span className="italic text-white/50">not included</span></h3>
              <ul className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {NOT_INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm font-light text-white/50">
                    <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-white/30" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="mt-24 border border-gold/20 bg-surface p-10 text-center md:p-14">
            <MapPin className="mx-auto h-8 w-8 text-gold" strokeWidth={1.25} />
            <h3 className="font-display mt-5 text-3xl tracking-tight text-white sm:text-4xl">You pick the <span className="italic text-gold">location.</span></h3>
            <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-white/55">
              Have a specific spot in mind? Tell us where you want the shoot. We'll coordinate the vehicle delivery to your selected location.
            </p>
            <p className="mt-4 text-xs font-light text-white/40">Additional delivery charges may apply depending on location and distance.</p>
          </div>
        </Reveal>

        <div className="mt-24">
          <SectionHeading eyebrow="Perfect For" title={<>Built for<br /><span className="italic text-gold">creators.</span></>} />
          <div className="grid grid-cols-2 gap-px border border-white/5 bg-white/5 lg:grid-cols-4">
            {PERFECT_FOR.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.label} delay={(i % 4) * 0.06} className="h-full">
                  <div data-testid={`photoshoot-use-${p.label.toLowerCase().replace(/[\s/]+/g, '-')}`} className="group flex h-full flex-col items-center bg-ink px-4 py-9 text-center transition-colors duration-500 hover:bg-surface">
                    <Icon className="h-6 w-6 text-gold transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.25} />
                    <span className="mt-4 text-xs font-semibold uppercase tracking-[0.15em] text-white/80 transition-colors duration-300 group-hover:text-gold">{p.label}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <div id="photoshoot-form" className="mt-24 scroll-mt-28">
          <SectionHeading eyebrow="Book Your Shoot" title={<>Request your<br /><span className="italic text-gold">photoshoot vehicle.</span></>} />
          <Reveal>
            <div className="relative border border-white/10 bg-surface p-8 md:p-10">
              <div className="absolute left-0 top-0 h-[2px] w-24 bg-gold" />
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    data-testid="photoshoot-success"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex min-h-[300px] flex-col items-center justify-center text-center"
                  >
                    <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1} />
                    <h3 className="font-display mt-6 text-3xl tracking-tight text-white">Request received.</h3>
                    <p className="mt-4 max-w-md text-base font-light leading-relaxed text-white/60">
                      Thank you! Your photoshoot request must be reviewed and confirmed by ALAS Velocity before the booking is finalized. We'll contact you shortly.
                    </p>
                    <button data-testid="photoshoot-again-button" onClick={() => { setDone(false); setForm(EMPTY); }} className="mt-8 border border-gold/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/10">
                      Make Another Request
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" data-testid="photoshoot-form-element" exit={{ opacity: 0 }} onSubmit={submit} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label="Full Name"><input data-testid="shoot-name-input" required value={form.full_name} onChange={set('full_name')} placeholder="Your full name" className={inputCls} /></Field>
                    <Field label="Phone"><input data-testid="shoot-phone-input" required type="tel" value={form.phone} onChange={set('phone')} placeholder="(305) 000-0000" className={inputCls} /></Field>
                    <Field label="Email"><input data-testid="shoot-email-input" required type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" className={inputCls} /></Field>
                    <Field label="Vehicle Requested">
                      <select data-testid="shoot-vehicle-select" required value={form.vehicle} onChange={set('vehicle')} className={`${inputCls} appearance-none`}>
                        <option value="" disabled className="bg-surface">Select a vehicle</option>
                        {VEHICLES.map((v) => (
                          <option key={v.id} value={v.name} className="bg-surface">{v.name}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Preferred Date"><input data-testid="shoot-date-input" required type="date" value={form.preferred_date} onChange={set('preferred_date')} className={inputCls} /></Field>
                    <Field label="Start Time"><input data-testid="shoot-time-input" required type="time" value={form.start_time} onChange={set('start_time')} className={inputCls} /></Field>
                    <Field label="Duration">
                      <select data-testid="shoot-duration-select" required value={form.duration} onChange={set('duration')} className={`${inputCls} appearance-none`}>
                        <option value="" disabled className="bg-surface">Select duration</option>
                        <option value="2 hours" className="bg-surface">2 hours</option>
                        <option value="4 hours" className="bg-surface">4 hours</option>
                        <option value="Other (discuss)" className="bg-surface">Other (discuss)</option>
                      </select>
                    </Field>
                    <Field label="Shoot Location"><input data-testid="shoot-location-input" required value={form.shoot_location} onChange={set('shoot_location')} placeholder="Address or landmark" className={inputCls} /></Field>
                    <Field label="Type of Shoot"><input data-testid="shoot-type-input" required value={form.shoot_type} onChange={set('shoot_type')} placeholder="Music video, fashion…" className={inputCls} /></Field>
                    <Field label="Number of People"><input data-testid="shoot-people-input" required value={form.num_people} onChange={set('num_people')} placeholder="e.g. 4" className={inputCls} /></Field>
                    <Field label="Vehicle Remains Stationary?">
                      <select data-testid="shoot-stationary-select" required value={form.stationary} onChange={set('stationary')} className={`${inputCls} appearance-none`}>
                        <option value="" disabled className="bg-surface">Select one</option>
                        <option value="yes" className="bg-surface">Yes</option>
                        <option value="no" className="bg-surface">No</option>
                      </select>
                    </Field>
                    <Field label="Repositioned During Shoot?">
                      <select data-testid="shoot-repositioned-select" required value={form.repositioned} onChange={set('repositioned')} className={`${inputCls} appearance-none`}>
                        <option value="" disabled className="bg-surface">Select one</option>
                        <option value="yes" className="bg-surface">Yes</option>
                        <option value="no" className="bg-surface">No</option>
                      </select>
                    </Field>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <Field label="Additional Notes">
                        <textarea data-testid="shoot-notes-textarea" rows={3} value={form.notes} onChange={set('notes')} placeholder="Anything we should know about the shoot…" className={`${inputCls} resize-none`} />
                      </Field>
                    </div>
                    {error && <p data-testid="shoot-form-error" className="sm:col-span-2 lg:col-span-3 text-sm text-red-400">{error}</p>}
                    <div className="sm:col-span-2 lg:col-span-3">
                      <button data-testid="shoot-submit-button" type="submit" disabled={sending} className="group flex w-full items-center justify-center gap-3 bg-gold py-5 text-xs font-semibold uppercase tracking-[0.25em] text-ink shadow-[0_0_25px_rgba(200,168,93,0.25)] transition-all duration-300 hover:bg-gold-bright disabled:opacity-60">
                        {sending ? 'Sending…' : 'Request Photoshoot'}
                        {!sending && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="photoshoot-policy" className="mt-8 border border-white/10 bg-ink p-7">
              <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold/80">Important Policy</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-white/55">
                The vehicle is provided specifically for the approved photoshoot/content-production period. The customer does not receive unrestricted rental use of the vehicle unless separately booked under a standard rental agreement. The vehicle may only be used in accordance with the approved booking, location, duration, and applicable vehicle/insurance requirements. Driving the vehicle during a photoshoot is not permitted unless specifically approved by ALAS Velocity.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Photoshoot;
